import React, { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Box,
  Tooltip,
  MenuItem,
  Typography,
  CircularProgress,
} from "@mui/material";
import TablePagination from "@/Components/Tables/Pagination";
import InputTextField from "@/Components/Form/InputTextField";
import InputDropdown from "@/Components/Form/InputDropdown";
import { useUser } from "@/Context/UserContext";
import { hookMap } from "@/redux/hookMap";

const TableWithFieldGenerator = forwardRef(({
  visibleFields = [],
  actions = [],
  groupedHeaders = [],
  compKey,
  tableSx = {},
}, ref) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fieldOptions, setFieldOptions] = useState({});

  const {
    formData,setFormData,
    setIsRequiredFieldFilled,
    isDataFetched,
  } = useUser();

  const [tableData, setTableData] = useState(formData[compKey] || [])

  useEffect(() => {
    if (formData && compKey in formData) {
      setTableData(formData[compKey]);
    }
  }, [formData, compKey]);

  const displayValue = (val) =>
    val !== undefined && val !== null && val !== "" ? val.toString() : "-";
  const getAllApiFields = (fields, parentKey = null) => {
    const result = [];
    fields.forEach((field, idx) => {
      if (field.endpoint) {
        result.push({
          ...field,
          parentKey: parentKey,
          subIndex: parentKey ? idx : null,
        });
      }
      if (Array.isArray(field.subFields)) {
        result.push(...getAllApiFields(field.subFields, field.key));
      }
    });
    return result;
  };

  const apiFields = getAllApiFields(visibleFields);

  const apiResults = apiFields
    .filter((field) => !!field.name)
    .map((field) => {
      const useApiHook = hookMap[field.endpoint];
      return {
        fieldName: field.name,
        result: useApiHook ? useApiHook() : {},
      };
    });

    useEffect(() => {
      apiResults.forEach(({ fieldName, result }) => {
        if (!fieldName) return;
    
        if (result?.data) {
    
          const mappedOptions = result.data.map((item) => ({
            label: item?.name || item?.code || "N/A",
            name: item?.name || "",
            value: item?.id || "",
         }));
    
          setFieldOptions((prev) => {
            if (JSON.stringify(prev[fieldName]) === JSON.stringify(mappedOptions)) {
              return prev;
            }
            return { ...prev, [fieldName]: mappedOptions };
          });
        } else {
          console.log("⚠️ No data returned for:", fieldName, result);
        }
      });
    }, [apiResults]);
    
  const handleInputChange = (
    eventOrValue,
    rowId,
    key,
    subIndex = null,
    type,
    subType = null
  ) => {
    let newValue = eventOrValue?.target?.value ?? eventOrValue;

    // Only allow numbers if type/subType is "number"
    if (type === "number" || subType === "number") {
      newValue = newValue.replace(/[^0-9]/g, "");
    }

    // when changing the field, only set it in the formDataJson and pass it to the
    setFormData((prev) => {
      const existingRow = prev[rowId] || {};
      const existingField = existingRow[key] || [];

      if (subIndex !== null) {
        const newSubValues = Array.isArray(existingField)
          ? [...existingField]
          : [];
        newSubValues[subIndex] = newValue;
        return {
          ...prev,
          [rowId]: {
            ...existingRow,
            [key]: newSubValues,
          },
        };
      }

      return {
        ...prev,
        [rowId]: {
          ...existingRow,
          [key]: newValue,
        },
      };
    });
    setIsRequiredFieldFilled(true);
  };

  const normalizedFields = visibleFields.map((field) =>
    typeof field === "string"
      ? {
        key: field.toLowerCase(),
        label: field.charAt(0).toUpperCase() + field.slice(1),
        textAlign: "left",
        width: `${100 / visibleFields.length}%`,
        editable: false,
        disabled: false,
        height: "44px",
        type: "text",
        hide: false,
      }
      : {
        name: field.name,
        key: field?.key?.toLowerCase(),
        label: field?.label,
        width: field?.width || `${100 / visibleFields.length}%`,
        truncate: field?.truncate || false,
        textAlign: field?.textAlign || "left",
        editable: field?.editable || false,
        disabled: field.disabled || false,
        height: field.height || "44px",
        type: field.type || "text",
        options: field.options || [],
        adornment: field.adornment || null,
        hide: typeof field.hide === "function" ? field.hide : field.hide,
        subFields: field.subFields || null,
        visibleOnRowIds: field.visibleOnRowIds || null,
      }
  );

  // Pass the updated local formDataJson state values to the handleSave function
  useImperativeHandle(ref, () => ({
      submit: () => {
        return formData || {};
      },
    }));

  if (!isDataFetched) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
          width: "100%",
        }}
      >
        <CircularProgress size={40} sx={{ color: "var(--brand-600)" }} />
      </Box>
    );
  }

  return (
    <Box
      className="table-generator"
      sx={{ width: "100%", maxWidth: "100vw", overflowX: "auto", ...tableSx, }}
    >
      <Table
        sx={{ tableLayout: "auto", width: "100%", borderCollapse: "collapse" }}
      >
        <TableHead>
          <TableRow sx={{ backgroundColor: "var(--brand-300)" }}>
            {groupedHeaders.map((header, index) => (
              <TableCell
                key={index}
                colSpan={header.colSpan}
                sx={{
                  backgroundColor: "var(--brand-200)",
                  textAlign: "center",
                  borderRight:
                    index < groupedHeaders.length
                      ? "1px solid var(--gray-0)"
                      : "none",
                  height: header.height || "auto",
                  width: header.width || "auto",
                }}
              >
                <Typography variant="bodySmall">{header.label}</Typography>
              </TableCell>
            ))}
            {actions.length > 0 && (
              <TableCell
                sx={{
                  backgroundColor: "var(--brand-200)",
                  width: "150px",
                  textAlign: "center",
                }}
              >
                <Typography variant=" bodySmallMedium">Actions</Typography>
              </TableCell>
            )}
          </TableRow>

          {/* ---------- FIELD LABELS ---------- */}
          <TableRow sx={{ backgroundColor: "var(--brand-100)" }}>
            {normalizedFields.map((field, index) => (
              <TableCell
                key={field.key}
                sx={{
                  height: "44px",
                  padding: "5px",
                  backgroundColor: "var(--brand-100)",
                  minWidth: field.width,
                  maxWidth: field.width,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  borderRight:
                    index < normalizedFields.length
                      ? "1px solid var(--gray-0)"
                      : "none",
                  display: field.hide ? "none" : "table-cell",
                }}
              >
                <Typography variant=" bodySmallMedium">
                  {field.label}
                </Typography>
              </TableCell>
            ))}
            {actions.length > 0 && (
              <TableCell sx={{ width: "150px" }}>
                <Typography variant=" bodySmallMedium" />
              </TableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {tableData
            ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((row, index) => {
              const actualIndex = page * rowsPerPage + index;
              const rowId = row.id ?? `row-${actualIndex}`;

              const normalizedRow = Object.fromEntries(
                Object.entries(row).map(([key, value]) => [
                  key.toLowerCase(),
                  value,
                ])
              );

              return (
                <TableRow
                  key={rowId}
                  sx={{ borderBottom: "1px solid var(--gray-200)" }}
                >
                  {normalizedFields.map((field, idx) => {
                    const isHidden =
                      typeof field.hide === "function"
                        ? field.hide(row)
                        : field.hide;
                    if (isHidden) return null;

                    const isDisabled =
                      typeof field.disabled === "function"
                        ? field.disabled(row, actualIndex)
                        : field.disabled;

                    const cellData = normalizedRow[field.key];

                    return (
                      <TableCell
                        key={field.key}
                        sx={{
                          backgroundColor: "var(--gray-0)",
                          textAlign: "center",
                          width: field.width,
                          minWidth: field.width,
                          maxWidth: field.width,
                          height: field.height,
                          padding: "8px",
                          whiteSpace: "nowrap",
                          borderRight:
                            idx < normalizedFields.length
                              ? "1px solid var(--brand-90)"
                              : "none",
                        }}
                      >
                        {/* ✅ SubFields Rendering */}
                        {Array.isArray(field.subFields) ? (
                          (field.visibleOnRowIds
                            ? field.visibleOnRowIds.includes(rowId)
                            : true) && (
                            <Box display="flex" alignItems="center" gap={1}>
                              {field.subFields.map((subField, subIdx) => {
                                const subValue =
                                  formData[rowId]?.[field.key]?.[subIdx] ??
                                  (Array.isArray(cellData)
                                    ? cellData[subIdx]
                                    : (subField.value ?? ""));
                                // 🔹 Label handling
                                const renderLabel = (
                                  subField,
                                  row,
                                  actualIndex
                                ) => {
                                  if (subField.label === true) {
                                    return <Box sx={{ height: "18px" }} />;
                                  } else if (
                                    typeof subField.label === "string"
                                  ) {
                                    return (
                                      <Typography
                                        variant="bodySmall"
                                        sx={{ textAlign: "left" }}
                                      >
                                        {subField.label}
                                      </Typography>
                                    );
                                  } else if (
                                    typeof subField.label === "function"
                                  ) {
                                    return (
                                      <Typography
                                        variant="bodySmall"
                                        sx={{ textAlign: "left" }}
                                      >
                                        {subField.label(row, actualIndex)}
                                      </Typography>
                                    );
                                  }
                                  // false or undefined → nothing
                                  return null;
                                };

                                // 🔹 Editable text (with optional icon inside)
                                if (subField.type === "text") {
                                  const isAutocomplete = true;
                                  return (
                                    <Box
                                      key={subIdx}
                                      display="flex"
                                      flexDirection="column"
                                      gap={1.3}
                                      width={field.width || "100%"}
                                    >
                                      {/* ✅ Show label if provided and not false */}
                                      {renderLabel(subField, row, actualIndex)}

                                      <InputTextField
                                        value={subValue}
                                        options={
                                          subField?.endpoint
                                            ? fieldOptions[subField?.name] || []
                                            : fieldOptions[subField.name] || []
                                        }
                                        sx={{
                                        }}
                                        onChange={(e) =>
                                          handleInputChange(
                                            e,
                                            rowId,
                                            field.key,
                                            subIdx,
                                            subField.type,
                                            subField.subType
                                          )
                                        }
                                        freeSolo={isAutocomplete}
                                        disabled={
                                          isDisabled || subField.disabled
                                        }
                                        style={{ width: subField.width }}
                                        InputProps={
                                          subField.icon
                                            ? {
                                              endAdornment: (
                                                <IconButton
                                                  size="small"
                                                  onClick={() =>
                                                    subField.onClick?.(row)
                                                  }
                                                >
                                                  {React.createElement(
                                                    subField.icon
                                                  )}
                                                </IconButton>
                                              ),
                                            }
                                            : {}
                                        }
                                      />
                                    </Box>
                                  );
                                }

                                if (subField.type === "dropdown") {
                                  return (
                                    <Box
                                      key={subIdx}
                                      display="flex"
                                      flexDirection="column" // ✅ vertical stacking
                                      gap={0.5}
                                      sx={{ width: subField.width }}
                                    >
                                      {/* ✅ Top label */}
                                      {renderLabel(subField, row, actualIndex)}

                                      {/* Dropdown input */}
                                      <InputDropdown
                                        id={`${rowId}-${field.key}-${subIdx}`}
                                        value={subValue}
                                        placeholder={subField.placeholder || ""}
                                        onChange={(e) =>
                                          handleInputChange(
                                            e,
                                            rowId,
                                            field.key,
                                            subIdx
                                          )
                                        }
                                        options={subField.options || []}
                                        disabled={
                                          isDisabled || subField.disabled
                                        }
                                        sx={{
                                          flex: 1,
                                          minWidth: 0,
                                          width: subField.endpoint
                                            ? "150px"
                                            : subField.width || "120px",
                                        }}
                                        // InputProps={
                                        //   subField.icon
                                        //     ? {
                                        //       endAdornment: (
                                        //         <IconButton
                                        //           size="small"
                                        //           onClick={() =>
                                        //             subField.onClick?.(row)
                                        //           }
                                        //         >
                                        //           {React.createElement(
                                        //             subField.icon
                                        //           )}
                                        //         </IconButton>
                                        //       ),
                                        //     }
                                        //     : {}
                                        // }
                                      />
                                    </Box>
                                  );
                                }

                                if (subField.type === "static") {
                                  return (
                                    <Box
                                      key={subIdx}
                                      display="flex"
                                      flexDirection="column"
                                      gap={0.5}
                                      sx={{
                                        width: subField.width || "auto",
                                        textAlign: "center",
                                      }}
                                    >
                                      {/* ✅ Show label if provided and not false */}
                                      {renderLabel(subField, row, actualIndex)}

                                      <Typography variant="bodySmallMedium">
                                        {subValue}
                                      </Typography>
                                    </Box>
                                  );
                                }

                                return null;
                              })}
                            </Box>
                          )
                        ) : field.editable ? (
                          field.type === "dropdown" ? (
                            <InputDropdown
                              gap={false}
                              fullWidth
                              value={
                                formData[rowId]?.[field.key] ??
                                cellData ??
                                ""
                              }
                              onChange={(e) =>
                                handleInputChange(e, rowId, field.key)
                              }
                              size="small"
                              disabled={isDisabled}
                              sx={{
                                borderRadius: "10px",
                                width: field.width,
                                backgroundColor: isDisabled
                                  ? "var(--gray-100)"
                                  : "var(--gray-0)",
                                cursor: isDisabled ? "not-allowed" : "text",
                                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "var(--gray-200)",
                                },
                              }}
                            >
                              {field.options.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </InputDropdown>
                          ) : (
                            <InputTextField
                              gap={false}
                              value={
                                formData[rowId]?.[field.key] ??
                                cellData ??
                                ""
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  e,
                                  rowId,
                                  field.key,
                                  null,
                                  field.type
                                )
                              }
                              disabled={isDisabled}
                              height={30}
                              sx={{
                                borderRadius: "10px",
                                backgroundColor: isDisabled
                                  ? "var(--gray-100)"
                                  : "var(--gray-0)",
                                cursor: isDisabled ? "not-allowed" : "text",
                                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "var(--gray-200)",
                                },
                              }}
                            />
                          )
                        ) : (
                          <Typography variant="bodySmallMedium">
                            {displayValue(cellData)}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}

                  {actions.length > 0 && (
                    <TableCell>
                      <Box display="flex" gap={2}>
                        {actions.map(({ icon, title, onClick }, idx) => (
                          <Tooltip title={title || ""} key={idx}>
                            <IconButton
                              size="small"
                              onClick={() => onClick?.(row)}
                            >
                              {icon}
                            </IconButton>
                          </Tooltip>
                        ))}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
        </TableBody>
      </Table>

      {tableData?.length > 10 && (
        <TablePagination
          count={tableData?.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      )}
    </Box>
  );
})

export default TableWithFieldGenerator;